import { useState, MouseEvent } from 'react';
import { Box, Popover } from '@mui/material';
import { MoreHoriz } from '@mui/icons-material';
import { AnalyticsColors } from './analyticsUi';

export interface ActionMenuItem {
    label: string;
    icon: React.ReactNode;
    onClick: () => void;
    destructive?: boolean;
    ownerOnly?: boolean;
}

interface ActionMenuProps {
    items: ActionMenuItem[];
    isOwner: boolean;
}

const ActionMenu = ({ items, isOwner }: ActionMenuProps) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    const visibleItems = items.filter((item) => !item.ownerOnly || isOwner);

    if (visibleItems.length === 0) return null;

    const handleOpen = (e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        setAnchorEl(e.currentTarget);
    };

    const handleClose = () => setAnchorEl(null);

    return (
        <>
            <Box
                component="button"
                onClick={handleOpen}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 28,
                    height: 28,
                    border: 'none',
                    background: 'transparent',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    color: AnalyticsColors.textTertiary,
                    transition: 'all 0.15s',
                    '&:hover': {
                        background: '#F3F4F6',
                        color: AnalyticsColors.textPrimary,
                    },
                }}
            >
                <MoreHoriz sx={{ fontSize: 16 }} />
            </Box>
            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{
                    paper: {
                        sx: { borderRadius: '10px', boxShadow: '0 4px 20px rgba(0,0,0,0.12)' },
                    },
                }}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 180, py: '4px' }}>
                    {visibleItems.map((item) => (
                        <Box
                            component="button"
                            key={item.label}
                            onClick={() => {
                                handleClose();
                                item.onClick();
                            }}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                px: '14px',
                                py: '8px',
                                border: 'none',
                                background: 'transparent',
                                cursor: 'pointer',
                                fontSize: 13,
                                fontWeight: 500,
                                color: item.destructive
                                    ? AnalyticsColors.danger
                                    : AnalyticsColors.textPrimary,
                                transition: 'background 0.1s',
                                textAlign: 'left',
                                width: '100%',
                                '&:hover': {
                                    background: item.destructive ? '#FEE2E2' : '#F3F4F6',
                                },
                                '& svg': {
                                    flexShrink: 0,
                                },
                            }}
                        >
                            {item.icon}
                            {item.label}
                        </Box>
                    ))}
                </Box>
            </Popover>
        </>
    );
};

export default ActionMenu;
